<script setup lang="ts">
/**
 * Filter drawer for the investor bank-accounts directory.
 *
 * Slides in from the right. Carries the first-name / last-name /
 * investor-email / bank-name / verification status / currency filters.
 * The admin-service endpoint exposes firstName + lastName +
 * investorEmail + bankName + accountStatus + currencyCode + investorId.
 */
import { reactive, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import Input from '@uiKit/elements/Input.vue'
import Select from '@uiKit/elements/Select.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import FormField from '@uiKit/components/FormField.vue'
import { BANK_ACCOUNT_STATUS_OPTIONS } from '../utils/bankAccountsDirectoryFormatters'
import type { BankAccountStatus } from '../types/bankAccountsDirectoryTypes'

export interface BankAccountsDirectoryFilterValues {
  firstName: string
  lastName: string
  investorEmail: string
  bankName: string
  accountStatus: BankAccountStatus | null
  currencyCode: string
}

interface Props {
  visible: boolean
  values: BankAccountsDirectoryFilterValues
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'apply', values: BankAccountsDirectoryFilterValues): void
  (e: 'reset'): void
}>()

const draft = reactive<BankAccountsDirectoryFilterValues>({
  firstName: props.values.firstName,
  lastName: props.values.lastName,
  investorEmail: props.values.investorEmail,
  bankName: props.values.bankName,
  accountStatus: props.values.accountStatus,
  currencyCode: props.values.currencyCode,
})

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return
    draft.firstName = props.values.firstName
    draft.lastName = props.values.lastName
    draft.investorEmail = props.values.investorEmail
    draft.bankName = props.values.bankName
    draft.accountStatus = props.values.accountStatus
    draft.currencyCode = props.values.currencyCode
  },
)

function handleApply() {
  emit('apply', {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    investorEmail: draft.investorEmail.trim(),
    bankName: draft.bankName.trim(),
    accountStatus: draft.accountStatus,
    currencyCode: draft.currencyCode.trim(),
  })
  emit('update:visible', false)
}

function handleReset() {
  draft.firstName = ''
  draft.lastName = ''
  draft.investorEmail = ''
  draft.bankName = ''
  draft.accountStatus = null
  draft.currencyCode = ''
  emit('reset')
}

function handleClose() {
  emit('update:visible', false)
}
</script>

<template>
  <AppDrawer
    :model-value="visible"
    position="right"
    width="440px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader
        title="Filter bank accounts"
        subtitle="Refine the directory"
        @close="handleClose"
      >
        <template #extras>
          <Button label="Reset" variant="text" color="secondary" @click="handleReset" />
        </template>
      </DrawerHeader>
    </template>

    <Box flex direction="col" class="drawer-body">
      <Box flex direction="col" gap="4" class="drawer-body__content">
        <FormField label="First name">
          <Input v-model="draft.firstName" placeholder="First name contains…" />
        </FormField>
        <FormField label="Last name">
          <Input v-model="draft.lastName" placeholder="Last name contains…" />
        </FormField>
        <FormField label="Investor email">
          <Input v-model="draft.investorEmail" placeholder="Email contains…" />
        </FormField>
        <FormField label="Bank">
          <Input v-model="draft.bankName" placeholder="Bank name contains…" />
        </FormField>
        <FormField label="Verification status">
          <Select
            v-model="draft.accountStatus"
            :options="[...BANK_ACCOUNT_STATUS_OPTIONS]"
            option-label="label"
            option-value="value"
            placeholder="Any status"
            show-clear
          />
        </FormField>
        <FormField label="Currency">
          <Input v-model="draft.currencyCode" placeholder="AED" />
        </FormField>
      </Box>

      <Box class="drawer-body__actions">
        <Button label="Apply filters" color="primary" block size="large" @click="handleApply" />
      </Box>
    </Box>
  </AppDrawer>
</template>

<style scoped>
.drawer-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.drawer-body__content {
  flex: 1;
}

.drawer-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

@media (max-width: 992px) {
  .drawer-body {
    padding: 1rem;
  }
}
</style>
